import {
    AlertTriangle,
    ChevronLeft,
    ChevronRight,
    Info,
    LoaderCircle,
    Plus,
    Sparkles,
    Tag,
    Trash2,
} from 'lucide-react'
import React, { useContext, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './ReviseIncentiveComponentScreen.module.css'

const TYPE_META = {
    SERVICE_COMMISSION: { label: 'Service Commission', color: '#1565c0', bg: '#e3f2fd' },
    RETAIL_COMMISSION: { label: 'Retail Commission', color: '#00695c', bg: '#e0f2f1' },
    CATEGORY_COMMISSION: { label: 'Category Commission', color: '#6a1b9a', bg: '#f3e5f5' },
    SLAB_COMMISSION: { label: 'Tiered Slab', color: '#e65100', bg: '#fff3e0' },
    TARGET_BONUS: { label: 'Target Bonus', color: '#2e7d32', bg: '#e8f5e9' },
    ATTENDANCE_BONUS: { label: 'Attendance Bonus', color: '#283593', bg: '#e8eaf6' },
    CUSTOM_INCENTIVE: { label: 'Custom Incentive', color: '#546e7a', bg: '#eceff1' },
}

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const asText = (value, fallback = '') => {
    if (value === null || value === undefined) return fallback
    return String(value)
}

const toNumber = (value) => {
    if (value === '' || value === null || value === undefined) return 0
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
}

const ReviseIncentiveComponentScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { templateIdentifier, componentIdentifier } = useParams()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const component = location.state?.component || null
    const routeTemplateName = location.state?.templateName || 'Commission Plan'
    const componentType = component?.componentType || ''

    const initialSlabs = Array.isArray(component?.slabs) && component.slabs.length > 0
        ? component.slabs.map((slab) => ({
            slabSequence: slab.slabSequence,
            revenueFrom: asText(slab.revenueFrom, '0'),
            revenueTo: slab.revenueTo !== null ? asText(slab.revenueTo, '') : null,
            commissionPercent: asText(slab.commissionPercent, ''),
            slabMode: slab.slabMode || 'ON_INCREMENTAL',
        }))
        : [{ slabSequence: 1, revenueFrom: '0', revenueTo: '', commissionPercent: '', slabMode: 'ON_INCREMENTAL' }]

    const initialCategoryRates = Array.isArray(component?.categoryRates) && component.categoryRates.length > 0
        ? component.categoryRates.map((rate) => ({
            serviceCategoryName: rate.serviceCategoryName || '',
            serviceCategoryCode: rate.serviceCategoryCode || '',
            commissionRate: asText(rate.commissionRate, ''),
        }))
        : [{ serviceCategoryName: '', serviceCategoryCode: '', commissionRate: '' }]

    const [formData, setFormData] = useState({
        componentName: component?.componentName || '',
        computationBase: component?.computationBase || 'SERVICE_REVENUE',
        rateValue: asText(component?.rateValue, ''),
        targetAmount: asText(component?.targetAmount, ''),
        targetPeriod: component?.targetPeriod || 'MONTHLY',
        bonusValue: asText(component?.bonusValue, ''),
        bonusValueType: component?.bonusValueType || 'FLAT',
        minServiceCountForEligibility: asText(component?.minServiceCountForEligibility ?? 0, '0'),
        cappedAt: Number(component?.cappedAt) > 0 ? asText(component?.cappedAt) : '',
        sortOrder: asText(component?.sortOrder, ''),
        slabs: initialSlabs,
        categoryRates: initialCategoryRates,
    })
    const [errors, setErrors] = useState({})
    const [isSaving, setIsSaving] = useState(false)

    const typeMeta = useMemo(
        () => TYPE_META[componentType] || { label: componentType || 'Component', color: '#475569', bg: '#e2e8f0' },
        [componentType]
    )

    const setField = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }))
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }))
    }

    const addSlabRow = () => {
        setFormData((prev) => ({
            ...prev,
            slabs: [
                ...prev.slabs,
                {
                    slabSequence: prev.slabs.length + 1,
                    revenueFrom: '',
                    revenueTo: '',
                    commissionPercent: '',
                    slabMode: 'ON_INCREMENTAL',
                },
            ],
        }))
    }

    const removeSlabRow = (index) => {
        setFormData((prev) => {
            const next = prev.slabs.filter((_, rowIndex) => rowIndex !== index)
                .map((row, rowIndex) => ({ ...row, slabSequence: rowIndex + 1 }))
            return {
                ...prev,
                slabs: next.length > 0 ? next : [{ slabSequence: 1, revenueFrom: '0', revenueTo: '', commissionPercent: '', slabMode: 'ON_INCREMENTAL' }],
            }
        })
    }

    const updateSlabRow = (index, field, value) => {
        setFormData((prev) => ({
            ...prev,
            slabs: prev.slabs.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row)),
        }))
        if (errors.slabs) setErrors((prev) => ({ ...prev, slabs: undefined }))
    }

    const addCategoryRow = () => {
        setFormData((prev) => ({
            ...prev,
            categoryRates: [...prev.categoryRates, { serviceCategoryName: '', serviceCategoryCode: '', commissionRate: '' }],
        }))
    }

    const removeCategoryRow = (index) => {
        setFormData((prev) => {
            const next = prev.categoryRates.filter((_, rowIndex) => rowIndex !== index)
            return {
                ...prev,
                categoryRates: next.length > 0 ? next : [{ serviceCategoryName: '', serviceCategoryCode: '', commissionRate: '' }],
            }
        })
    }

    const updateCategoryRow = (index, field, value) => {
        setFormData((prev) => ({
            ...prev,
            categoryRates: prev.categoryRates.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row)),
        }))
        if (errors.categoryRates) setErrors((prev) => ({ ...prev, categoryRates: undefined }))
    }

    const validate = () => {
        const nextErrors = {}
        const type = componentType

        if (!formData.componentName.trim()) nextErrors.componentName = 'Component name cannot be empty.'

        if (type === 'SERVICE_COMMISSION' || type === 'RETAIL_COMMISSION') {
            const rate = Number(formData.rateValue)
            if (!rate || rate <= 0 || rate > 100) {
                nextErrors.rateValue = 'Enter a commission % between 0.01 and 100.'
            }
        }

        if (type === 'TARGET_BONUS') {
            if (!formData.targetAmount || Number(formData.targetAmount) <= 0) nextErrors.targetAmount = 'Target amount is required.'
            if (!formData.bonusValue || Number(formData.bonusValue) <= 0) nextErrors.bonusValue = 'Bonus value is required.'
        }

        if (type === 'ATTENDANCE_BONUS') {
            if (!formData.bonusValue || Number(formData.bonusValue) <= 0) nextErrors.bonusValue = 'Bonus amount is required.'
        }

        if (type === 'SLAB_COMMISSION') {
            if (formData.slabs.length === 0) {
                nextErrors.slabs = 'At least one slab row is required.'
            } else {
                const hasTopSlab = formData.slabs.some((slab) => slab.revenueTo === null || slab.revenueTo === '')
                if (!hasTopSlab) nextErrors.slabs = 'The last slab must have no upper limit.'

                const invalidPercent = formData.slabs.some((slab) => !slab.commissionPercent || Number(slab.commissionPercent) <= 0)
                if (invalidPercent) nextErrors.slabs = 'Each slab must have a valid commission %.'
            }
        }

        if (type === 'CATEGORY_COMMISSION') {
            if (formData.categoryRates.length === 0) {
                nextErrors.categoryRates = 'Add at least one category rate.'
            } else {
                const hasInvalidRow = formData.categoryRates.some(
                    (rate) => !rate.serviceCategoryCode.trim() || !rate.commissionRate || Number(rate.commissionRate) <= 0
                )
                if (hasInvalidRow) nextErrors.categoryRates = 'All categories must have a code and commission %.'
            }
        }

        return nextErrors
    }

    const reviseIncentiveComponent = async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier || !templateIdentifier || !componentIdentifier) return
        if (!component) {
            toast.error('Component context missing. Please open this screen from the template detail page.')
            navigate(-1)
            return
        }

        const nextErrors = validate()
        if (Object.keys(nextErrors).length > 0) {
            setErrors(nextErrors)
            return
        }
        setErrors({})

        try {
            setIsSaving(true)

            const payload = {
                token: authCtx.token,
                storeIdentifier: activeStore.storeIdentifier,
                templateIdentifier,
                componentIdentifier,
                componentName: formData.componentName.trim(),
                sortOrder: parseInt(formData.sortOrder, 10) || component.sortOrder,
                minServiceCountForEligibility: parseInt(formData.minServiceCountForEligibility, 10) || 0,
                cappedAt: toNumber(formData.cappedAt),
            }

            if (!['RETAIL_COMMISSION', 'ATTENDANCE_BONUS'].includes(componentType)) {
                payload.computationBase = formData.computationBase
            }

            if (['SERVICE_COMMISSION', 'RETAIL_COMMISSION', 'CUSTOM_INCENTIVE'].includes(componentType)) {
                payload.rateValue = toNumber(formData.rateValue)
            }

            if (componentType === 'TARGET_BONUS') {
                payload.targetAmount = toNumber(formData.targetAmount)
                payload.targetPeriod = formData.targetPeriod
                payload.bonusValue = toNumber(formData.bonusValue)
                payload.bonusValueType = formData.bonusValueType
            }

            if (componentType === 'ATTENDANCE_BONUS' || componentType === 'CUSTOM_INCENTIVE') {
                payload.bonusValue = toNumber(formData.bonusValue)
            }

            if (componentType === 'SLAB_COMMISSION' && formData.slabs.length > 0) {
                payload.slabs = formData.slabs.map((slab, index) => ({
                    slabSequence: index + 1,
                    revenueFrom: toNumber(slab.revenueFrom),
                    revenueTo: slab.revenueTo === '' || slab.revenueTo === null ? null : toNumber(slab.revenueTo),
                    commissionPercent: toNumber(slab.commissionPercent),
                    slabMode: slab.slabMode || 'ON_INCREMENTAL',
                }))
            }

            if (componentType === 'CATEGORY_COMMISSION' && formData.categoryRates.length > 0) {
                payload.categoryRates = formData.categoryRates.map((rate) => ({
                    serviceCategoryName: rate.serviceCategoryName.trim(),
                    serviceCategoryCode: rate.serviceCategoryCode.trim().toUpperCase(),
                    commissionRate: toNumber(rate.commissionRate),
                }))
            }

            const response = await fetch(BASE_URL_API + 'reviseIncentiveComponent', {
                method: 'POST',
                body: JSON.stringify(payload),
            })

            if (!response.ok) {
                toast.error('Something went wrong. Please try again.')
                return
            }

            const respo = await response.json()

            if (respo?.status === 'success') {
                const count = respo?.response?.changeCount ?? 1
                toast.success(`Updated — ${count} change${count !== 1 ? 's' : ''} saved.`)
                navigate(-1)
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'VAL_001':
                case 'VAL_002':
                case 'VAL_003':
                    toast.error(respo?.message || 'Please check input values.')
                    break
                case 'VAL_007':
                    toast.error('Component no longer exists.')
                    navigate(-1)
                    break
                case 'VAL_008':
                    if ((respo?.message || '').toLowerCase().includes('no changes')) {
                        toast.info('No changes to save.')
                    } else {
                        toast.error(respo?.message || 'Cannot revise this component.')
                        navigate(-1)
                    }
                    break
                case 'COMP_004':
                    toast.error(respo?.message || 'Template not found.')
                    navigate(-1)
                    break
                case 'COMP_006':
                    setErrors((prev) => ({ ...prev, slabs: respo?.message || 'Invalid slab configuration.' }))
                    break
                case 'SYS_002':
                    toast.error(respo?.message || 'Revision failed. Changes rolled back.')
                    break
                default:
                    toast.error(respo?.message || 'Failed to save revision.')
                    break
            }
        } catch (error) {
            console.error('reviseIncentiveComponent error:', error)
            toast.error('Network error. Please check your connection.')
        } finally {
            setIsSaving(false)
        }
    }

    const renderTypeSpecificFields = () => {
        if (componentType === 'SERVICE_COMMISSION' || componentType === 'RETAIL_COMMISSION') {
            return (
                <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>Commission % <span className={styles.required}>*</span></label>
                    <input
                        className={`${styles.textInput} ${errors.rateValue ? styles.inputError : ''}`}
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={formData.rateValue}
                        onChange={(event) => setField('rateValue', event.target.value)}
                    />
                    {errors.rateValue && <p className={styles.fieldError}><AlertTriangle size={12} /> {errors.rateValue}</p>}
                </div>
            )
        }

        if (componentType === 'SLAB_COMMISSION') {
            return (
                <>
                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Computation Base</label>
                        <select
                            className={styles.selectInput}
                            value={formData.computationBase}
                            onChange={(event) => setField('computationBase', event.target.value)}
                        >
                            <option value="SERVICE_REVENUE">Service Revenue</option>
                            <option value="RETAIL_REVENUE">Retail Revenue</option>
                            <option value="TOTAL_REVENUE">Total Revenue</option>
                        </select>
                    </div>

                    <div className={styles.builderCard}>
                        <div className={styles.builderHeader}>
                            <p>Slab Structure</p>
                            <button type="button" className={styles.addRowBtn} onClick={addSlabRow}>
                                <Plus size={13} /> Add Slab
                            </button>
                        </div>

                        {formData.slabs.map((slab, index) => {
                            const isLast = index === formData.slabs.length - 1
                            return (
                                <div key={`slab-${index}`} className={styles.builderRow}>
                                    <div className={styles.builderGridSlab}>
                                        <input
                                            className={styles.textInput}
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={slab.revenueFrom}
                                            onChange={(event) => updateSlabRow(index, 'revenueFrom', event.target.value)}
                                            placeholder="From ₹"
                                        />
                                        <select
                                            className={styles.selectInput}
                                            value={slab.revenueTo === null ? 'NO_LIMIT' : slab.revenueTo}
                                            onChange={(event) => updateSlabRow(index, 'revenueTo', event.target.value === 'NO_LIMIT' ? null : event.target.value)}
                                        >
                                            <option value="">To ₹</option>
                                            {isLast && <option value="NO_LIMIT">No limit</option>}
                                        </select>
                                        <input
                                            className={styles.textInput}
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="0.01"
                                            value={slab.commissionPercent}
                                            onChange={(event) => updateSlabRow(index, 'commissionPercent', event.target.value)}
                                            placeholder="Commission %"
                                        />
                                        <select
                                            className={styles.selectInput}
                                            value={slab.slabMode}
                                            onChange={(event) => updateSlabRow(index, 'slabMode', event.target.value)}
                                        >
                                            <option value="ON_INCREMENTAL">Incremental</option>
                                            <option value="ON_TOTAL">On Total</option>
                                        </select>
                                    </div>

                                    <button type="button" className={styles.removeRowBtn} onClick={() => removeSlabRow(index)}>
                                        <Trash2 size={13} />
                                    </button>
                                </div>
                            )
                        })}

                        {errors.slabs && <p className={styles.fieldError}><AlertTriangle size={12} /> {errors.slabs}</p>}
                    </div>
                </>
            )
        }

        if (componentType === 'CATEGORY_COMMISSION') {
            return (
                <>
                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Computation Base</label>
                        <select
                            className={styles.selectInput}
                            value={formData.computationBase}
                            onChange={(event) => setField('computationBase', event.target.value)}
                        >
                            <option value="SERVICE_REVENUE">Service Revenue</option>
                            <option value="TOTAL_REVENUE">Total Revenue</option>
                        </select>
                    </div>

                    <div className={styles.builderCard}>
                        <div className={styles.builderHeader}>
                            <p>Category Rates</p>
                            <button type="button" className={styles.addRowBtn} onClick={addCategoryRow}>
                                <Plus size={13} /> Add Category
                            </button>
                        </div>

                        {formData.categoryRates.map((row, index) => (
                            <div key={`category-${index}`} className={styles.builderRow}>
                                <div className={styles.builderGridCategory}>
                                    <input
                                        className={styles.textInput}
                                        value={row.serviceCategoryName}
                                        onChange={(event) => updateCategoryRow(index, 'serviceCategoryName', event.target.value)}
                                        placeholder="Category name"
                                    />
                                    <input
                                        className={styles.textInput}
                                        value={row.serviceCategoryCode}
                                        onChange={(event) => updateCategoryRow(index, 'serviceCategoryCode', event.target.value.toUpperCase())}
                                        placeholder="Code"
                                    />
                                    <input
                                        className={styles.textInput}
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        value={row.commissionRate}
                                        onChange={(event) => updateCategoryRow(index, 'commissionRate', event.target.value)}
                                        placeholder="%"
                                    />
                                </div>
                                <button type="button" className={styles.removeRowBtn} onClick={() => removeCategoryRow(index)}>
                                    <Trash2 size={13} />
                                </button>
                            </div>
                        ))}

                        {errors.categoryRates && <p className={styles.fieldError}><AlertTriangle size={12} /> {errors.categoryRates}</p>}
                    </div>
                </>
            )
        }

        if (componentType === 'TARGET_BONUS') {
            return (
                <>
                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Computation Base</label>
                        <select
                            className={styles.selectInput}
                            value={formData.computationBase}
                            onChange={(event) => setField('computationBase', event.target.value)}
                        >
                            <option value="SERVICE_REVENUE">Service Revenue</option>
                            <option value="RETAIL_REVENUE">Retail Revenue</option>
                            <option value="TOTAL_REVENUE">Total Revenue</option>
                        </select>
                    </div>

                    <div className={styles.splitRow}>
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>Target Amount (₹) <span className={styles.required}>*</span></label>
                            <input
                                className={`${styles.textInput} ${errors.targetAmount ? styles.inputError : ''}`}
                                type="number"
                                min="0"
                                step="0.01"
                                value={formData.targetAmount}
                                onChange={(event) => setField('targetAmount', event.target.value)}
                            />
                            {errors.targetAmount && <p className={styles.fieldError}><AlertTriangle size={12} /> {errors.targetAmount}</p>}
                        </div>
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>Target Period</label>
                            <select
                                className={styles.selectInput}
                                value={formData.targetPeriod}
                                onChange={(event) => setField('targetPeriod', event.target.value)}
                            >
                                <option value="MONTHLY">Monthly</option>
                                <option value="WEEKLY">Weekly</option>
                            </select>
                        </div>
                    </div>

                    <div className={styles.splitRow}>
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>Bonus Value (₹ / %) <span className={styles.required}>*</span></label>
                            <input
                                className={`${styles.textInput} ${errors.bonusValue ? styles.inputError : ''}`}
                                type="number"
                                min="0"
                                step="0.01"
                                value={formData.bonusValue}
                                onChange={(event) => setField('bonusValue', event.target.value)}
                            />
                            {errors.bonusValue && <p className={styles.fieldError}><AlertTriangle size={12} /> {errors.bonusValue}</p>}
                        </div>
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>Bonus Type</label>
                            <select
                                className={styles.selectInput}
                                value={formData.bonusValueType}
                                onChange={(event) => setField('bonusValueType', event.target.value)}
                            >
                                <option value="FLAT">Flat Amount</option>
                                <option value="PERCENT_OF_EXCESS">% of Excess</option>
                            </select>
                        </div>
                    </div>
                </>
            )
        }

        if (componentType === 'ATTENDANCE_BONUS') {
            return (
                <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>Bonus Amount (₹) <span className={styles.required}>*</span></label>
                    <input
                        className={`${styles.textInput} ${errors.bonusValue ? styles.inputError : ''}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.bonusValue}
                        onChange={(event) => setField('bonusValue', event.target.value)}
                    />
                    {errors.bonusValue && <p className={styles.fieldError}><AlertTriangle size={12} /> {errors.bonusValue}</p>}
                </div>
            )
        }

        if (componentType === 'CUSTOM_INCENTIVE') {
            return (
                <>
                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Computation Base</label>
                        <select
                            className={styles.selectInput}
                            value={formData.computationBase}
                            onChange={(event) => setField('computationBase', event.target.value)}
                        >
                            <option value="SERVICE_REVENUE">Service Revenue</option>
                            <option value="RETAIL_REVENUE">Retail Revenue</option>
                            <option value="TOTAL_REVENUE">Total Revenue</option>
                            <option value="FIXED">Fixed</option>
                        </select>
                    </div>

                    <div className={styles.splitRow}>
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>Commission %</label>
                            <input
                                className={styles.textInput}
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                value={formData.rateValue}
                                onChange={(event) => setField('rateValue', event.target.value)}
                                placeholder="0 if flat"
                            />
                        </div>
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>Flat Amount (₹)</label>
                            <input
                                className={styles.textInput}
                                type="number"
                                min="0"
                                step="0.01"
                                value={formData.bonusValue}
                                onChange={(event) => setField('bonusValue', event.target.value)}
                                placeholder="0 if %"
                            />
                        </div>
                    </div>
                </>
            )
        }

        return null
    }

    if (!component) {
        return (
            <div className={styles.page}>
                <header className={styles.header}>
                    <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                        <ChevronLeft size={20} />
                    </button>
                    <div className={styles.headerMeta}>
                        <span className={styles.heroPill}><Sparkles size={12} /> Revise Rule</span>
                        <h1 className={styles.headerTitle}>{routeTemplateName}</h1>
                    </div>
                </header>

                <div className={styles.formWrap}>
                    <div className={styles.errorBanner}>
                        <AlertTriangle size={16} />
                        <p>Component data missing. Open this screen from the component edit button.</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ChevronLeft size={20} />
                </button>
                <div className={styles.headerMeta}>
                    <span className={styles.heroPill}><Sparkles size={12} /> Revise Rule</span>
                    <h1 className={styles.headerTitle}>{routeTemplateName}</h1>
                </div>
            </header>

            <div className={styles.formWrap}>
                <div className={styles.infoBanner}>
                    <Info size={15} className={styles.infoBannerIcon} />
                    <p>
                        Editing <strong>{component.componentName}</strong>. Saving creates a revised active version and keeps historical trace.
                    </p>
                </div>

                <div className={styles.typeChipRow}>
                    <span className={styles.typeChip} style={{ color: typeMeta.color, background: typeMeta.bg }}>
                        <Tag size={12} /> {typeMeta.label}
                    </span>
                </div>

                <div className={styles.formCard}>
                    <div className={styles.formCardHeader}>
                        <h3>Rule Configuration</h3>
                        <ChevronRight size={15} />
                    </div>

                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Component Name <span className={styles.required}>*</span></label>
                        <input
                            className={`${styles.textInput} ${errors.componentName ? styles.inputError : ''}`}
                            value={formData.componentName}
                            onChange={(event) => setField('componentName', event.target.value)}
                            maxLength={200}
                        />
                        {errors.componentName && <p className={styles.fieldError}><AlertTriangle size={12} /> {errors.componentName}</p>}
                    </div>

                    {renderTypeSpecificFields()}

                    <div className={styles.splitRow}>
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>Min Services</label>
                            <input
                                className={styles.textInput}
                                type="number"
                                min="0"
                                step="1"
                                value={formData.minServiceCountForEligibility}
                                onChange={(event) => setField('minServiceCountForEligibility', event.target.value)}
                            />
                        </div>
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>Cap Amount (₹)</label>
                            <input
                                className={styles.textInput}
                                type="number"
                                min="0"
                                step="0.01"
                                value={formData.cappedAt}
                                onChange={(event) => setField('cappedAt', event.target.value)}
                                placeholder="0 = No cap"
                            />
                        </div>
                    </div>

                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Sort Order</label>
                        <input
                            className={styles.textInput}
                            type="number"
                            min="0"
                            step="1"
                            value={formData.sortOrder}
                            onChange={(event) => setField('sortOrder', event.target.value)}
                        />
                    </div>
                </div>

                <button
                    type="button"
                    className={`${styles.saveBtn} ${isSaving ? styles.saveBtnDisabled : ''}`}
                    disabled={isSaving}
                    onClick={reviseIncentiveComponent}
                >
                    {isSaving
                        ? <><LoaderCircle size={16} className={styles.spin} /> Saving...</>
                        : <>Save Changes</>}
                </button>
            </div>
        </div>
    )
}

export default ReviseIncentiveComponentScreen